import { useEffect, useState } from "react"
import XMPPError from "@xmpp/error"

// How long a saved pick stays on screen waiting for the chats node to confirm
// it; a pubsub event that never comes must not leave the cell lying.
const PENDING_TIMEOUT_MS = 10_000

/**
 * A crew board cell's pick while its save is on the way: shown in place of
 * `current` (what the store says) until the store changes in any way, the
 * pick landing or another dispatcher's save, or until 10 s after the save
 * came back. A failed save drops it at once and toasts the error.
 */
export const usePendingPick = (current: string) => {
  const [pick, setPick] = useState<{ value: string; from: string } | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (pick && current !== pick.from) setPick(null)
  }, [pick, current])

  useEffect(() => {
    if (!pick || saving) return
    const timer = setTimeout(() => setPick(null), PENDING_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [pick, saving])

  const save = async (value: string, write: () => Promise<void>) => {
    setPick({ value, from: current })
    setSaving(true)
    try {
      await write()
    } catch (e) {
      setPick(null)
      window.appToaster.show({
        message: e instanceof XMPPError ? e.text : (e as Error).message,
        intent: "danger",
        timeout: 3000,
      })
    } finally {
      setSaving(false)
    }
  }

  return { pending: pick?.value, saving, save }
}
